import { ApolloError } from '@apollo/client'
import { showNotificationToast } from 'shared/utils/notify'
import { shortenAddress } from 'shared/utils/shorten-address'

/** Backend messages of linkWalletChallenge / linkWallet → what the user reads. */
const MESSAGES: Array<[RegExp, string]> = [
  [
    /already linked to your account/i,
    'This wallet is already linked to your account.'
  ],
  [
    /already used by another account/i,
    'This wallet belongs to another XYRO account and cannot be linked. A wallet that has already played without being linked belongs to its own account.'
  ],
  [/challenge expired or missing/i, 'The signing request expired. Try again.'],
  [/invalid signature/i, 'The signature did not match the wallet. Try again.'],
  [/invalid solana wallet address/i, 'This is not a valid Solana address.']
]

const getMessage = (error: unknown) =>
  error instanceof ApolloError ?
    (error.graphQLErrors[0]?.message ?? error.message)
  : error instanceof Error ? error.message
  : ''

export const getLinkWalletErrorMessage = (error: unknown): string => {
  const message = getMessage(error)

  return (
    MESSAGES.find(([pattern]) => pattern.test(message))?.[1] ??
    (message || 'Something went wrong. Try again.')
  )
}

export const notifyLinkWalletError = (error: unknown) => {
  console.error('Failed to link wallet', error)
  showNotificationToast({
    type: 'error',
    title: 'Could not link the wallet',
    description: getLinkWalletErrorMessage(error)
  })
}

export const notifySignatureDeclined = () =>
  showNotificationToast({ type: 'info', title: 'Signature declined' })

export const notifyAlreadyLinked = (address: string) =>
  showNotificationToast({
    type: 'info',
    title: 'Already linked',
    description: `${shortenAddress(address)} is one of your wallets and is now selected for play.`
  })

export const notifyWalletLinked = (address: string) =>
  showNotificationToast({
    type: 'success',
    title: 'Wallet linked',
    description: `${shortenAddress(address)} is linked and selected for play.`
  })

const NOT_LINKED = /not linked to your account/i
const PRIMARY_NOT_UNLINKABLE = /primary wallet cannot be unlinked/i

/**
 * `setPrimaryWallet` / `unlinkWallet` refused because the wallet left the
 * account meanwhile: unlinked in another tab, or in Privy (mirrored by the
 * backend's webhook).
 */
export const isWalletNotLinkedError = (error: unknown) =>
  NOT_LINKED.test(getMessage(error))

/** Backend messages of setPrimaryWallet / unlinkWallet → what the user reads. */
export const getWalletActionErrorMessage = (error: unknown): string => {
  const message = getMessage(error)

  if (NOT_LINKED.test(message)) {
    return 'This wallet is no longer linked to your account.'
  }
  if (PRIMARY_NOT_UNLINKABLE.test(message)) {
    return 'Make another wallet primary before unlinking this one.'
  }
  return 'Something went wrong. Try again.'
}

export const notifyWalletActionError = (title: string, error: unknown) => {
  console.error(title, error)
  showNotificationToast({
    type: 'error',
    title,
    description: getWalletActionErrorMessage(error)
  })
}

export const notifyPrimaryChanged = (address: string) =>
  showNotificationToast({
    type: 'success',
    title: 'Primary wallet changed',
    description: `${shortenAddress(address)} is now your primary wallet.`
  })

export const notifyWalletUnlinked = (address: string) =>
  showNotificationToast({
    type: 'success',
    title: 'Wallet unlinked',
    description: `${shortenAddress(address)} is no longer linked to your account.`
  })
